// MATERIAL UI
import { Button, CircularProgress, Grid, Typography } from "@mui/material";

// COMPONENTS
import {
  StyledAvatar,
  StyledNextButton,
} from "src/components/wallets/common/styles/CryptoWithdrawalStyled";
import { TwoFAModal } from "src/components/modal/TwoFAModal";
import { ButtonContainer } from "./ButtonContainer";
import { AlertCard } from "src/components/alerts/AlertCard";
import { StyledError } from "src/components/wallets/Modal/Withdraw/Error";

// STYLES
import { PaymentStyles } from "./Payment.styles";

// REDUX
import { useDispatch } from "react-redux";
import { setError, setStep, useBeneficiarySlice } from "src/store/features/beneficiary.slice";
import { useUserSlice } from "src/store/features/user.slice";

// HOOKS
import { useBankDetails } from "src/utils/hooks/banking/useBankDetails";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

export const Summary = (): JSX.Element => {
  const dispatch = useDispatch();
  const classes = PaymentStyles();

  /*********
   * HOOKS *
   *********/
  const {
    currency,
    beneficiaryName,
    beneficiaryAddress,
    bankName,
    bankAddress,
    swiftCode,
    transitNumber,
    institutionNumber,
    abaRoutingNumber,
    ibanAccNumber,
    sortCodeOrBsb,
    error,
    identityType,
    accountType,
  } = useBeneficiarySlice();

  const { is2FAEnabled } = useUserSlice();

  /*************
   * HOOK DATA *
   *************/
  const {
    isModalOpen,
    handle2faVerification,
    isSendingEmail,
    handleModal,
    isButtonDisabled,
    handleSubmitBankDetails,
    isAddingBankDetailsLoading,
  } = useBankDetails();

  /************
   * HANDLERS *
   ************/
  const handleAddDetails = () => {
    dispatch(setStep("AddDetails"));
    trackEvent("Client clicked on Add Bank Details", {}, "Payment");
  };

  const beneficiaryAddressValues = Object.values(beneficiaryAddress).filter((value) => value);
  const bankAddressValues = Object.values(bankAddress).filter((value) => value);

  const isGbpOrEur = currency.value === "GBP" || currency.value === "EUR";
  const isUsdOrGbp = currency.value === "USD" || currency.value === "GBP";
  const isAud = currency.value === "AUD";
  const isUsd = currency.value === "USD";
  const isGbp = currency.value === "GBP";

  return (
    <>
      <Grid item width="100%">
        {!is2FAEnabled && (
          <AlertCard
            titleText={"Action Required"}
            subText={"It looks like you don’t have 2FA enabled, click"}
            link={"/settings/security"}
            linkText={"here"}
          />
        )}
        <Typography className={classes.bankManagementSubHeader} style={{ textAlign: "center" }}>
          Review Bank Details
        </Typography>
      </Grid>
      <Grid item width="100%" paddingTop={"12px !important"}>
        <Typography className={classes.summaryHeader}>Currency</Typography>
        <Grid item display="flex" flexDirection="row" alignItems="center">
          <StyledAvatar src={currency.image} />
          <Typography>{currency.value}</Typography>
        </Grid>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Account Holder Name</Typography>
        <Typography>{beneficiaryName}</Typography>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Account Holder Address</Typography>
        <Grid item width="100%">
          <Typography>{beneficiaryAddressValues.join(", ")}</Typography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        justifyContent="space-between"
        flexDirection="row"
        rowSpacing={3}
        columnSpacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.summaryHeader}>Identity Type</Typography>
          <Typography>{identityType}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.summaryHeader}>Account Type</Typography>
          <Typography>{accountType}</Typography>
        </Grid>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Bank Name</Typography>
        <Typography>{bankName}</Typography>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Bank Address</Typography>
        <Grid item width="100%">
          <Typography>{bankAddressValues.join(", ")}</Typography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        justifyContent="space-between"
        flexDirection="row"
        rowSpacing={3}
        columnSpacing={3}>
        {transitNumber && (
          <Grid item xs={12} md={6}>
            <Typography className={classes.summaryHeader}>Transit Number</Typography>
            <Typography>{transitNumber}</Typography>
          </Grid>
        )}
        {institutionNumber && (
          <Grid item xs={12} md={6}>
            <Typography className={classes.summaryHeader}>Institution Number</Typography>
            <Typography>{institutionNumber}</Typography>
          </Grid>
        )}
        {abaRoutingNumber && (
          <Grid item xs={12} md={isUsd ? 4 : 6}>
            <Typography className={classes.summaryHeader}>ABA Routing Number</Typography>
            <Typography>{abaRoutingNumber}</Typography>
          </Grid>
        )}
        {sortCodeOrBsb && (
          <Grid item xs={12} md={isGbp ? 4 : 6}>
            <Typography className={classes.summaryHeader}>{isAud ? "BSB" : "Sort code"}</Typography>
            <Typography>{sortCodeOrBsb}</Typography>
          </Grid>
        )}
        <Grid item xs={12} md={isUsdOrGbp ? 4 : 6}>
          <Typography className={classes.summaryHeader}>
            {isGbpOrEur ? "IBAN" : "Account Number"}
          </Typography>
          <Typography>{ibanAccNumber}</Typography>
        </Grid>
        {swiftCode && (
          <Grid item xs={12} md={isUsdOrGbp ? 4 : 6}>
            <Typography className={classes.summaryHeader}>Swift Code</Typography>
            <Typography>{swiftCode}</Typography>
          </Grid>
        )}
      </Grid>

      <TwoFAModal
        title="Verification"
        emailTemplate="bankDetails2FA"
        isOpen={isModalOpen}
        handleClose={handleModal}
        setError={setError}
        isLoading={isAddingBankDetailsLoading}>
        <Grid item marginTop="50px">
          <StyledNextButton
            disabled={isButtonDisabled}
            variant="text"
            onClick={handleSubmitBankDetails}>
            Submit
            {isAddingBankDetailsLoading && (
              <CircularProgress size={20} style={{ marginLeft: "10px" }} />
            )}
          </StyledNextButton>
          {error && <StyledError>{error}</StyledError>}
        </Grid>
      </TwoFAModal>

      <ButtonContainer>
        <Button
          disableRipple
          variant="text"
          onClick={handleAddDetails}
          className={classes.secondaryButton}>
          Edit
        </Button>
        <Button
          disableRipple
          variant="text"
          onClick={handle2faVerification}
          disabled={isSendingEmail}
          className={classes.primaryButton}>
          Submit
        </Button>
      </ButtonContainer>
    </>
  );
};
